import React, { useEffect, useState, useContext } from "react";
import { Layout } from "@shopify/polaris";
import { useLazyQuery } from "@apollo/react-hooks";
import "jspdf-autotable";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import context
import { PrivateContext } from "lib/context";

// import constant
import constant from "lib/constant/constant";

// import components
import { Banner, Spinner } from "lib/components";

// import query
import { GET_ORDER_LINES, GET_PAYMENT_EXPORT_SETTING } from "app/payments/apollo/queries";

// import helpers
import { baseHelper, fileHelper, storageHelper } from "lib/helpers";
import paymentHelper from "./helper/payment";

const ExportAsPDF = () => {
  const data = useContext(PrivateContext);
  const { cms, currentUser } = data;
  const brandLogo = storageHelper.get("logoUrl");
  const defaultLogo = constant.DEFAULT_PDF_LOGO;
  const isVendor = baseHelper.isVendor(currentUser);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [queryData, setQueryData] = useState();
  const [
    getOrderLines,
    { data: getOrderData, loading: getOrderLoading, error: getOrderError },
  ] = useLazyQuery(GET_ORDER_LINES, { variables: { input: queryData } });

  const [getPaymentSetting, { data: getSettingData, loading: settingLoading, error: settingError }] = useLazyQuery(
    GET_PAYMENT_EXPORT_SETTING
  );

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  useEffect(() => {
    if (!getOrderLoading && !getOrderError && getOrderData && getSettingData && !settingLoading && !settingError) {
      const resData = baseHelper.getResponseData(getOrderData, constant.gql.GET_ORDER_LINES);
      const settingResponse = baseHelper.getResponseData(getSettingData, constant.gql.GET_PAYMENT_EXPORT_SETTING);
      let bannerData = false;
      if (!resData) {
        const resError = baseHelper.getResponseError(getOrderData, constant.gql.GET_ORDER_LINES);
        bannerData = {
          title: resError,
          status: constant.CRITICAL,
          isOpen: true,
        };
      }
      if (!resData.length) {
        bannerData = {
          title: cms("common.message.error.noPayment"),
          status: constant.CRITICAL,
          isOpen: true,
        };
      }
      if (bannerData) {
        setBanner(bannerData);
        setTimeout(() => {
          window.close();
        }, 2000);
        return;
      }

      const pdfData = paymentHelper.paymentCSVData(resData, settingResponse, false, true, isVendor);
      if (brandLogo) {
        fileHelper
          .loadImage(brandLogo)
          .then((logo) => {
            fileHelper.generatePdf(logo, pdfData, constant.CONSOLIDATED_INVOICE);
          })
          .catch(() => {
            fileHelper.generatePdf(false, pdfData, constant.CONSOLIDATED_INVOICE);
          });
      } else {
        fileHelper
          .loadImage(defaultLogo)
          .then((logo) => {
            fileHelper.generatePdf(logo, pdfData, constant.CONSOLIDATED_INVOICE);
          })
          .catch(() => {
            fileHelper.generatePdf(false, pdfData, constant.CONSOLIDATED_INVOICE);
          });
      }
    }
  }, [
    brandLogo,
    cms,
    defaultLogo,
    getOrderData,
    getOrderError,
    getOrderLoading,
    getSettingData,
    isVendor,
    settingError,
    settingLoading,
  ]);

  useEffect(() => {
    const exportPayment = () => {
      const dataToFetch = {};
      const {
        accountingStatus,
        endDate,
        fulfillmentStatus,
        startDate,
        status,
        taxStatus,
        vendor,
      } = baseHelper.queryParamsFromLocation(data);
      if (status && status.length) {
        dataToFetch.status = status.split(",");
      }
      if (fulfillmentStatus && fulfillmentStatus.length) {
        dataToFetch.fulfillmentStatus = fulfillmentStatus.split(",");
      }
      if (vendor && vendor.length) {
        dataToFetch.vendor = vendor.split(",");
      }
      if (accountingStatus && accountingStatus.length) {
        dataToFetch.accountingStatus = accountingStatus.split(",");
      }
      if (startDate && endDate) {
        dataToFetch.dateRange = { start: startDate, end: endDate };
      }
      if (taxStatus && taxStatus.length) {
        dataToFetch.taxStatus = taxStatus.split(",");
      }
      setQueryData(dataToFetch);
      getOrderLines();
      getPaymentSetting();
    };
    if (isPageLoading) {
      exportPayment();
      setIsPageLoading(false);
    }
  }, [data, getOrderLines, getPaymentSetting, isPageLoading]);

  return (
    <Layout>
      {getOrderLoading && <Spinner isFullPage />}
      <Layout.Section>
        {banner.isOpen && (
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
          />
        )}
      </Layout.Section>
    </Layout>
  );
};

export default withFeature(withErrorBoundary(ExportAsPDF), { feature: constant.EXPORT_PAYMENT });
